// src/pages/Collection/MyCollection.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getUserCollection, getAllCards } from '../../api/cardService';
import CardBacksGallery from '../../components/cards/CardBacksGallery';
import CardModal from '../../components/cards/CardModal';
import MyDecks from '../Deck/MyDecks';
import './MyCollection.css';

const MyCollection = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialView = searchParams.get('tab') === 'decks' ? 'decks' : 'cards';
  const [view, setView] = useState(initialView); // 'cards' | 'backs' | 'decks'
  const [cards, setCards] = useState([]);
  const [filteredCards, setFilteredCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    element: 'all',
    type: 'all',
    rarity: 'all',
    effect: 'all',
    search: ''
  });
  const [displayMode, setDisplayMode] = useState('list'); // 'list' | 'grid'
  const [gridColumns, setGridColumns] = useState(5);
  const [sortBy, setSortBy] = useState('default');
  const [selectedCard, setSelectedCard] = useState(null);
  
  const navigate = useNavigate();

  // Charger les cartes au montage
  useEffect(() => {
    const fetchCards = async () => {
      try {
        setLoading(true);
        
        // Essayer d'abord de récupérer la collection de l'utilisateur
        try {
          const userCollection = await getUserCollection();
          if (userCollection && userCollection.cards && userCollection.cards.length > 0) {
            console.log('User collection loaded:', userCollection.cards.length, 'cards');
            setCards(userCollection.cards);
          } else {
            // Fallback si la collection est vide
            const allCardsResponse = await getAllCards();
            console.log('All cards loaded:', allCardsResponse.cards.length, 'cards');
            setCards(allCardsResponse.cards);
          }
        } catch (err) {
          // Fallback à toutes les cartes en cas d'erreur
          console.log('Fallback to all cards due to error:', err);
          const allCardsResponse = await getAllCards({ limit: 1000 });
          console.log('All cards loaded (fallback):', allCardsResponse.cards.length, 'cards');
          setCards(allCardsResponse.cards);
        }
        
        setError(null);
      } catch (err) {
        setError('Erreur lors du chargement des cartes. Veuillez réessayer.');
        console.error('Error fetching cards:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCards();
  }, []);

  // Filtrer les cartes lorsque les filtres changent
  useEffect(() => {
    if (cards.length === 0) return; // Évite de filtrer si pas de cartes
    
    let result = [...cards];
    
    // Filtre par élément
    if (filters.element !== 'all') {
      result = result.filter(card => card.element === filters.element);
    }
    
    // Filtre par type
    if (filters.type !== 'all') {
      result = result.filter(card => card.type === filters.type);
    }
    
    // Filtre par rareté
    if (filters.rarity !== 'all') {
      result = result.filter(card => card.rarity === filters.rarity);
    }
    
    // Filtre par présence d'effet (ex: personnages avec/sans effet)
    if (filters.effect && filters.effect !== 'all') {
      const hasEffect = (c) => (Array.isArray(c.effects) && c.effects.length > 0) || !!c.effect || (Array.isArray(c.keywords) && c.keywords.length > 0) || (c.type === 'EQUIPMENT' && ((c.attackBonus || 0) > 0 || (c.defenseBonus || 0) > 0));
      result = result.filter(card => filters.effect === 'with' ? hasEffect(card) : !hasEffect(card));
    }

    // Filtre par recherche
    if (filters.search) {
      const searchTerm = filters.search.toLowerCase();
      result = result.filter(card => 
        card.name.toLowerCase().includes(searchTerm) || 
        (card.effect && card.effect.toLowerCase().includes(searchTerm))
      );
    }
    
    setFilteredCards(result);
  }, [filters, cards]);

  // Trier les cartes selon le tri choisi
  const sortCards = (list) => {
    const sorted = [...list];
    const rarityRank = { RAINBOW: 5, LEGENDARY: 4, EPIC: 3, SUPER_RARE: 3, SUPER_SUPER_RARE: 4, RARE: 2, COMMON: 1, NORMAL: 1 };
    switch (sortBy) {
      case 'cost-asc': sorted.sort((a, b) => (a.cost ?? 99) - (b.cost ?? 99)); break;
      case 'cost-desc': sorted.sort((a, b) => (b.cost ?? -1) - (a.cost ?? -1)); break;
      case 'name-asc': sorted.sort((a, b) => a.name.localeCompare(b.name, 'fr')); break;
      case 'name-desc': sorted.sort((a, b) => b.name.localeCompare(a.name, 'fr')); break;
      case 'atk-desc': sorted.sort((a, b) => (b.attack ?? -1) - (a.attack ?? -1)); break;
      case 'def-desc': sorted.sort((a, b) => (b.defense ?? -1) - (a.defense ?? -1)); break;
      case 'rarity-desc': sorted.sort((a, b) => (rarityRank[b.rarity] ?? 0) - (rarityRank[a.rarity] ?? 0)); break;
      case 'acquisition':
        sorted.sort((a, b) =>
          new Date(b.acquiredAt || b.obtainedAt || b.createdAt || 0) -
          new Date(a.acquiredAt || a.obtainedAt || a.createdAt || 0));
        break;
      default: break;
    }
    return sorted;
  };

  // Mettre à jour un filtre
  const handleFilterChange = (filterName, value) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      [filterName]: value
    }));
  };

  // Ouvrir le canvas de detail d'une carte
  const handleCardClick = (card) => {
    setSelectedCard(card);
  };

  // Changer d'onglet (met a jour l'URL)
  const switchView = (v) => {
    setView(v);
    if (v === 'decks') setSearchParams({ tab: 'decks' });
    else setSearchParams({});
  };

  if (loading) {
    return (
      <div className="collection-loading">
        <div className="loading-spinner"></div>
        <p>Chargement de votre collection...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="collection-error">
        <h2>Une erreur est survenue</h2>
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>Réessayer</button>
      </div>
    );
  }

  return (
    <div className="collection-container">
      <div className="collection-header">
        <p className="collection-kicker" aria-hidden="true">Grimoire du duelliste</p>
        <h1>Ma Collection</h1>
      </div>
      
      <div className="collection-view-toggle">
        <button
          className={view === 'cards' ? 'toggle-btn active' : 'toggle-btn'}
          onClick={() => switchView('cards')}
        >Mes cartes</button>
        <button
          className={view === 'decks' ? 'toggle-btn active' : 'toggle-btn'}
          onClick={() => switchView('decks')}
        >Mes decks</button>
        <button
          className={view === 'backs' ? 'toggle-btn active' : 'toggle-btn'}
          onClick={() => switchView('backs')}
        >Dos de cartes</button>
      </div>
      
      {view === 'backs' ? (
        <CardBacksGallery />
      ) : view === 'decks' ? (
        <MyDecks embedded />
      ) : (
      <>
      <div className="collection-stats">
        <p>Total: <span>{cards.length}</span> cartes</p>
        <p>Filtrées: <span>{filteredCards.length}</span> cartes</p>
      </div>
      
      <div className="collection-filters">
        <div className="filter-group">
          <label htmlFor="element-filter">Élément</label>
          <select 
            id="element-filter" 
            value={filters.element}
            onChange={(e) => handleFilterChange('element', e.target.value)}
          >
            <option value="all">Tous</option>
            <option value="FIRE">Feu</option>
            <option value="WATER">Eau</option>
            <option value="EARTH">Terre</option>
            <option value="AIR">Vent</option>
            <option value="LIGHT">Lumière</option>
            <option value="DARK">Ténèbres</option>
            <option value="RAINBOW">Arc-en-Ciel</option>
          </select>
        </div>
        
        <div className="filter-group">
          <label htmlFor="type-filter">Type</label>
          <select 
            id="type-filter" 
            value={filters.type}
            onChange={(e) => handleFilterChange('type', e.target.value)}
          >
            <option value="all">Tous</option>
            <option value="CHARACTER">Personnage</option>
            <option value="SPELL">Sort</option>
            <option value="TRAP">Piège</option>
            <option value="EQUIPMENT">Équipement</option>
            <option value="TERRAIN">Terrain</option>
          </select>
        </div>
        
        <div className="filter-group">
          <label htmlFor="rarity-filter">Rareté</label>
          <select 
            id="rarity-filter" 
            value={filters.rarity}
            onChange={(e) => handleFilterChange('rarity', e.target.value)}
          >
            <option value="all">Toutes</option>
            <option value="NORMAL">Normal (N)</option>
            <option value="RARE">Rare (R)</option>
            <option value="SUPER_RARE">Super Rare (SR)</option>
            <option value="SUPER_SUPER_RARE">Super Super Rare (SSR)</option>
            <option value="LEGENDARY">Légendaire (L)</option>
            <option value="RAINBOW">Arc-en-Ciel (AC)</option>
          </select>
        </div>
        
        <div className="filter-group">
          <label htmlFor="effect-filter">Effet</label>
          <select 
            id="effect-filter" 
            value={filters.effect || 'all'}
            onChange={(e) => handleFilterChange('effect', e.target.value)}
          >
            <option value="all">Tous</option>
            <option value="with">Avec effet</option>
            <option value="without">Sans effet</option>
          </select>
        </div>
        
        <div className="filter-group search-group">
          <label htmlFor="search-filter">Recherche</label>
          <input 
            id="search-filter" 
            type="text" 
            placeholder="Nom ou texte"
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
          />
        </div>
      </div>
      
      {filteredCards.length === 0 ? (
        <div className="no-cards-message">
          <p>Aucune carte ne correspond à ces critères.</p>
          <button onClick={() => setFilters({
            element: 'all',
            type: 'all',
            rarity: 'all',
            effect: 'all',
            search: ''
          })}>Réinitialiser les filtres</button>
        </div>
      ) : (
        <>
        {/* Barre d'affichage : liste / grille + colonnes */}
        <div className="collection-display-bar">
          <div className="display-mode-toggle">
            <button
              className={`mode-btn ${displayMode === 'list' ? 'active' : ''}`}
              onClick={() => setDisplayMode('list')}
              title="Affichage en liste"
              aria-label="Affichage en liste"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><rect x="1" y="2" width="5" height="5" rx="1"/><rect x="1" y="9" width="5" height="5" rx="1"/><rect x="8" y="3" width="7" height="1.5" rx=".75"/><rect x="8" y="6.5" width="7" height="1.5" rx=".75"/><rect x="8" y="10" width="7" height="1.5" rx=".75"/><rect x="8" y="13.5" width="7" height="1.5" rx=".75"/></svg>
              Liste
            </button>
            <button
              className={`mode-btn ${displayMode === 'grid' ? 'active' : ''}`}
              onClick={() => setDisplayMode('grid')}
              title="Affichage en grille"
              aria-label="Affichage en grille"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><rect x="1" y="1" width="6" height="6" rx="1"/><rect x="9" y="1" width="6" height="6" rx="1"/><rect x="1" y="9" width="6" height="6" rx="1"/><rect x="9" y="9" width="6" height="6" rx="1"/></svg>
              Grille
            </button>
          </div>
          {displayMode === 'grid' && (
            <div className="grid-columns-picker" title="Nombre de colonnes">
              <span className="cols-label">Colonnes</span>
              {[3, 4, 5, 6, 7, 8].map((n) => (
                <button
                  key={n}
                  className={`col-btn ${gridColumns === n ? 'active' : ''}`}
                  onClick={() => setGridColumns(n)}
                >{n}</button>
              ))}
            </div>
          )}
          <div className="sort-picker">
            <label htmlFor="sort-select" className="cols-label">Tri</label>
            <select id="sort-select" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="default">Par défaut</option>
              <option value="acquisition">Acquisition (récentes)</option>
              <option value="cost-asc">Coût croissant</option>
              <option value="cost-desc">Coût décroissant</option>
              <option value="name-asc">Nom A-Z</option>
              <option value="name-desc">Nom Z-A</option>
              <option value="atk-desc">Attaque décroissante</option>
              <option value="def-desc">PV décroissants</option>
              <option value="rarity-desc">Rareté décroissante</option>
            </select>
          </div>
        </div>

        {displayMode === 'list' ? (
        <div className="cards-list">
          {sortCards(filteredCards).map((card, index) => (
            <div
              key={`card-${card.id}-${index}`}
              className={`cl-row element-${card.element.toLowerCase()} rarity-glow-${card.rarity.toLowerCase()}`}
              onClick={() => handleCardClick(card)}
            >
              <div className="cl-art">
                <img
                  src={card.imageUrl || `/api/static/images/elements/${card.element.toLowerCase()}.svg`}
                  alt={card.name}
                  loading="lazy"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = `/api/static/images/card-backs/${card.element.toLowerCase()}-back.svg`;
                  }}
                />
                <span className={`cost-crystal element-${card.element.toLowerCase()}`}>{card.cost}</span>
              </div>
              <div className="cl-body">
                <div className="cl-head">
                  <h3 className="cl-name">{card.name}</h3>
                  <span className={`cl-rarity rarity-${card.rarity.toLowerCase()}`}>{card.rarity}</span>
                </div>
                <div className="cl-tags">
                  <span className={`cl-element element-${card.element.toLowerCase()}`}>{card.element}</span>
                  <span className="cl-type">{card.type}</span>
                  {card.type === 'CHARACTER' && (
                    <span className="cl-stats">
                      <b className="cl-atk">ATK {card.attack ?? '-'}</b>
                      <b className="cl-def">PV {card.defense ?? '-'}</b>
                    </span>
                  )}
                </div>
                {(card.description || card.effect) && (
                  <p className="cl-desc">{card.description || card.effect}</p>
                )}
              </div>
            </div>
          ))}
        </div>
        ) : (
        <div
          className="cards-grid"
          style={{ gridTemplateColumns: `repeat(${gridColumns}, minmax(0, 1fr))` }}
        >
          {sortCards(filteredCards).map((card, index) => (
            <div
              key={`card-${card.id}-${index}`}
              className={`card-item element-${card.element.toLowerCase()} rarity-glow-${card.rarity.toLowerCase()}`}
              onClick={() => handleCardClick(card)}
              title={card.name}
            >
              <div className="card-image">
                <img
                  src={card.imageUrl || `/api/static/images/elements/${card.element.toLowerCase()}.svg`}
                  alt={card.name}
                  loading="lazy"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = `/api/static/images/card-backs/${card.element.toLowerCase()}-back.svg`;
                  }}
                />
                <span className={`cost-crystal small element-${card.element.toLowerCase()}`}>{card.cost}</span>
              </div>
              <div className="card-info">
                <h3 className="card-name">{card.name}</h3>
              </div>
            </div>
          ))}
        </div>
        )}
        </>
      )}
      </>
      )}

      {selectedCard && (
        <CardModal card={selectedCard} onClose={() => setSelectedCard(null)} />
      )}
    </div>
  );
};

export default MyCollection;